import { createStore } from "vuex";
import { assignmentModule } from "@/courses/stores/assignment.js";
import { courseModule } from "@/courses/stores/course.js";
import { coursesModule } from "@/courses/stores/courses.js";
import { gradingStatusModule } from "@/courses/stores/gradingStatus.js";
import { pointsPlannerModule } from "@/courses/stores/pointsPlanner.js";
import { navigationModule } from "@/courses/stores/navigation.js";
import { unlocksModule } from "@/courses/stores/unlocks.js";
import { usersModule } from "@/courses/stores/users.js";
import { apiResponseToData } from "@/courses/utilities/apiRequestHelpers.js";
import { apiClient, requestHeader } from "@/courses/services/GenericService.js";

import {
  changeGuideAPIUrl,
  getCurrentUserURL,
  userFlagAPIUrl,
  courseStudentsAPIUrl,
  gradebookAssignmentsListAPIUrl,
  gradebookStudentIdsAPIUrl,
  toggleCourseMembershipStatusAPIUrl,
  toggleStudentActivationStatusAPIUrl,
} from "@/courses/urls.js";

const store = createStore({
  modules: {
    course: courseModule,
    assignment: assignmentModule,
    gradingStatus: gradingStatusModule,
    courses: coursesModule,
    users: usersModule,
    unlocks: unlocksModule,
    pointsPlanner: pointsPlannerModule,
    navigation: navigationModule,
  },
  state: {
    gradebookStudents: [],
    gradebookAssignments: [],
    errorAlertMessage: null,
    errorAlertMessageMoreInfo: [],
    successAlertMessage: null,
    scrollToLocation: null,
    newActivity: {
      courses: {},
      newInstructorsCount: 0,
      newSubscriptionsCount: 0,
      paidCoursesCount: 0,
    },
    currentSubscribedCourses: [],
    previouslySubscribedCourses: [],
    environmentTerms: {
      environmentName: "",
      teachingAssistant: "",
    },
    students: [],
    studentCount: 0,
    user: {
      id: null,
      firstName: "",
      lastName: "",
      email: "",
      username: "",
      showGuide: null,
      hasSeenOnboarding: null,
      lastLogin: null,
      lastLogout: null,
      createdAt: null,
      subscription: {},
      courseMembership: [
        {
          id: null,
          name: "",
          number: "",
          role: "",
          instructor: "",
          url: "",
          gradingStatus: {
            url: "",
            ungraded: null,
            ready: null,
            resubmissions: null,
          },
          eventCount: null,
          announcementCount: null,
          assignments: [
            {
              name: "",
              dueDate: "",
              planned: null,
              submitted: null,
              graded: null,
              url: "",
            },
          ],
          term: {
            name: "",
            year: "",
            start: "",
            end: "",
          },
          subscribed: null,
          published: null,
          active: null,
        },
      ],
    },
  },
  actions: {
    getGradebookAssignments: async function ({ commit }) {
      const url = gradebookAssignmentsListAPIUrl.stringify();
      const resp = await fetch(url);
      if (resp.status === 404) {
        console.log(resp.status);
      } else if (!resp.ok) {
        throw resp;
      }
      const assignmentsJSON = await resp.json();
      const assignments = apiResponseToData(assignmentsJSON);
      commit("setGradebookAssignments", assignments);
    },
    getGradebookStudents: async function ({ commit, dispatch }) {
      // Batching is used:
      const url = gradebookStudentIdsAPIUrl.stringify();
      const resp = await fetch(url);
      const students = await resp.json();

      let studentCount = students.length;
      commit("setStudentCount", studentCount);

      let chunk = 50;
      for (let index = 0, len = students.length; index < len; index += chunk) {
        const studentIds = students.slice(index, index + chunk);
        dispatch("getGradebookStudentsSubset", { studentIds });
      }
    },
    getGradebookStudentsSubset: async function ({ commit }, { studentIds }) {
      const resp = await fetch(
        `/api/gradebook/students?student_ids=${studentIds}`,
      );
      const json = await resp.json();
      const gradebookStudents = apiResponseToData(json);
      commit("setGradebookStudents", gradebookStudents);
    },
    toggleFlagControl({ commit }, student) {
      commit("toggleFlag", student);
    },
    flagStudent: async function ({ commit }, student) {
      let url = userFlagAPIUrl.stringify({ userId: student.id });
      const resp = await fetch(url, {
        method: "POST",
        headers: requestHeader,
        credentials: "same-origin",
      });
      if (!resp.ok) {
        let message = "There was an error flagging the student.";
        commit("addErrorAlertMessage", message);
      }
    },
    toggleCourseActivation: async function ({ commit }, payload) {
      const url = toggleStudentActivationStatusAPIUrl.stringify();
      const resp = await fetch(url, {
        method: "PUT",
        headers: requestHeader,
        credentials: "same-origin",
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        let message = `The request to update ${payload.student.first_name} ${payload.student.last_name}'s activation status was unsuccessful.`;
        commit("addErrorAlertMessage", message);
      } else {
        let toggleCheck = !payload.student.activated_for_course
          ? "activated for this course"
          : "deactivated for this course";
        let message = `${payload.student.first_name} ${payload.student.last_name} has been ${toggleCheck}`;
        commit("addSuccessAlertMessage", message);
        commit("toggleActivation", payload);
      }
    },
    deleteCourseMembership: async function ({ state, commit }, payload) {
      const url = toggleCourseMembershipStatusAPIUrl.stringify();
      const resp = await fetch(url, {
        method: "DELETE",
        headers: requestHeader,
        credentials: "same-origin",
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        let message = `There was an error removing ${payload.student.first_name} ${payload.student.last_name} from this course.`;
        commit("addErrorAlertMessage", message);
      } else {
        let message = `${payload.student.first_name} ${payload.student.last_name} was successfully removed from course.`;
        commit("addSuccessAlertMessage", message);
        commit("setMembershipStatus", payload);
        commit("setStudentCount", state.studentCount - 1);
      }
    },
    getAllStudents: async function ({ commit, dispatch }, courseId) {
      let url = courseStudentsAPIUrl.stringify({ courseId: courseId });
      const { data } = await apiClient.get(url, { params: { fetch_ids: 1 } });
      commit("setStudentCount", data["student_ids"].length);
      let chunk = 50;
      for (
        let index = 0, len = data["student_ids"].length;
        index < len;
        index += chunk
      ) {
        const studentIds = data["student_ids"].slice(index, index + chunk);
        dispatch("getStudentsSubset", { courseId, studentIds });
      }
    },
    getStudentsSubset: async function ({ commit }, { courseId, studentIds }) {
      let url = courseStudentsAPIUrl.stringify({ courseId: courseId });
      const { data } = await apiClient.get(url, {
        params: { fetch_ids: 0, student_ids: studentIds },
      });
      const students = apiResponseToData(data);
      commit("setAllStudents", students);
    },
    getCurrentUser: async function ({ commit }) {
      let url = getCurrentUserURL.stringify();
      const { data } = await apiClient.get(url);
      commit("setCurrentUser", data);
    },
    getCourseMemberships: async function ({ commit }) {
      const resp = await fetch("api/courses/top_level_dashboard");
      if (resp.status === 404) {
        console.log(resp.status);
      } else if (!resp.ok) {
        throw resp;
      }
      const json = await resp.json();
      const final = apiResponseToData(json);
      commit("addCourses", final);
    },
    addNewCourse: async function ({ commit }, { course, overview }) {
      try {
        const resp = await apiClient.post("/api/courses", course);
        if (resp.status != 200) {
          let message = "There was an error creating your new course.";
          commit("addErrorAlertMessage", message);
          console.error(resp.data);
        } else {
          if (overview) {
            store.dispatch("courses/getNewActivity");
          } else {
            store.dispatch("getCourseMemberships");
            commit("setScrollToLocation", "#unpublished_courses");
          }
          let message = "You’ve successfully created a new course!";
          commit("addSuccessAlertMessage", message);
        }
      } catch (e) {
        let message = "There was an error creating your new course.";
        commit("addErrorAlertMessage", message);
        console.error(e);
      }
    },
    seenOnboarding: async function ({ commit }) {
      await apiClient.put("/api/users/seen_onboarding");
      commit("setHasSeenOnboarding", true);
    },
    toggleGuideControl({ commit }) {
      commit("toggleGuide");
    },
    removeSuccessAlert({ commit }) {
      commit("removeSuccessAlert");
    },
    removeErrorAlert({ commit }) {
      commit("removeErrorAlert");
    },
    removeErrorAlertMessageMoreInfo({ commit }) {
      commit("removeErrorAlertMessageMoreInfo");
    },
    changeGuide: async function ({ state }) {
      const url = changeGuideAPIUrl.stringify();
      apiClient.put(url, {
        guideState: state.user.showGuide,
      });
    },
    addErrorAlertMessage({ commit }, message) {
      commit("addErrorAlertMessage", message);
    },
    addSuccessAlertMessage({ commit }, message) {
      commit("addSuccessAlertMessage", message);
    },
  },
  mutations: {
    setGradebookAssignments(state, assignments) {
      state.gradebookAssignments = [...assignments];
    },
    setGradebookStudents(state, students) {
      state.gradebookStudents = [...state.gradebookStudents, ...students];
    },
    setEnvironmentVariables(state, railsEnv) {
      if (railsEnv === "development") {
        state.environmentTerms.environmentName = "Local";
        state.environmentTerms.teachingAssistant = "teaching elves";
      }
      if (railsEnv === "staging") {
        state.environmentTerms.environmentName = "Staging";
        state.environmentTerms.teachingAssistant = "sous-chefs";
      }
      if (railsEnv === "production") {
        state.environmentTerms.environmentName = "Umich";
        state.environmentTerms.teachingAssistant = "GSIs";
      }
      if (railsEnv === "beta") {
        state.environmentTerms.environmentName = "App";
        state.environmentTerms.teachingAssistant = "teaching assistants";
      }
    },
    addCourses(state, courses) {
      state.user.courseMembership = courses.map((course) => {
        return {
          id: course.id,
          name: course.name,
          number: course.course_number,
          role: course.role,
          active: course.active,
          instructor: "",
          url: course.change_course_path,
          subscription: { ...course.subscription },
          gradingStatus: {
            url: course.grading_status_path,
            ungraded: course.ungraded,
            ready: course.ready_for_release,
            resubmissions: course.resubmissions,
          },
          eventCount: course.events_this_week,
          announcementCount: course.unread_announcements,
          assignments: { ...course.assignments },
          term: {
            name: course.semester,
            year: course.year,
            start: course.start_date,
            end: course.end_date,
          },
          subscribed: course.subscribed,
          published: course.published,
          termForAssignments: course.term_for_assignments,
          termForStudent: course.term_for_student,
          termForGSI: course.term_for_gsi,
        };
      });
      let subscribedCourses = state.user.courseMembership.filter((course) => {
        if (course.subscribed) {
          return course.subscription.user_id === state.user.id;
        }
        return false;
      });
      state.currentSubscribedCourses = subscribedCourses;
      state.previouslySubscribedCourses = subscribedCourses;
    },
    addNewActivity(
      state,
      { courses, newSubscriptionsCount, newInstructorsCount, paidCoursesCount },
    ) {
      state.newActivity.courses = courses.map((course) => {
        return {
          id: course.id,
          name: course.name,
          url: course.change_course_path,
          editURL: course.edit_course_path,
          active: course.active,
          published: course.published,
          ltiID: course.lti_uid,
          subscribed: course.subscribed,
          semester: course.semester,
          year: course.year,
          number: course.course_number,
          studentCount: course.student_count,
          created: course.formatted_created_at,
          instructors: { ...course.staff },
        };
      });
      state.newActivity.newSubscriptionsCount = newSubscriptionsCount;
      state.newActivity.newInstructorsCount = newInstructorsCount;
      state.newActivity.paidCoursesCount = paidCoursesCount;
    },
    toggleGuide(state) {
      state.user.showGuide = !state.user.showGuide;
    },
    setCurrentUser(state, user) {
      state.user.id = user.id;
      state.user.firstName = user.first_name;
      state.user.lastName = user.last_name;
      state.user.email = user.email;
      state.user.admin = user.admin;
      state.user.showGuide = user.show_guide;
      state.user.hasSeenOnboarding = user.has_seen_onboarding;
      state.user.avatarUrl = user.avatar_file_name?.url;
      state.user.role = user.role;
    },
    setScrollToLocation(state, location) {
      state.scrollToLocation = location;
    },
    addSuccessAlertMessage(state, message) {
      state.errorAlertMessage = null;
      state.successAlertMessage = message;
    },
    removeSuccessAlert(state) {
      state.successAlertMessage = null;
    },
    addErrorAlertMessage(state, message) {
      state.successAlertMessage = null;
      state.errorAlertMessage = message;
    },
    addErrorAlertMessageMoreInfo(state, moreInfo) {
      state.errorAlertMessageMoreInfo = moreInfo;
    },
    removeErrorAlert(state) {
      state.errorAlertMessage = null;
    },
    removeErrorAlertMessageMoreInfo(state) {
      state.errorAlertMessageMoreInfo = [];
    },
    setMembershipStatus(state, payload) {
      const studentIndex = state.students.findIndex(
        (element) => element.id == payload.student.id,
      );
      let tempState = [...this.state.students];
      tempState.splice(studentIndex, 1);
      state.students = tempState;
    },
    toggleActivation(state, payload) {
      state.students = [
        ...state.students.map((existingStudent) =>
          existingStudent.id !== payload.student.id
            ? existingStudent
            : {
                ...existingStudent,
                ...{
                  activated_for_course: !payload.student.activated_for_course,
                  rank: undefined,
                },
              },
        ),
      ];
    },
    toggleFlag(state, student) {
      state.students = [
        ...state.students.map((existingStudent) =>
          existingStudent.id !== student.id
            ? existingStudent
            : { ...existingStudent, ...{ flagged: !student.flagged } },
        ),
      ];
    },
    setAllStudents(state, students) {
      state.students = [...state.students, ...students];
    },
    setStudentCount(state, studentCount) {
      state.studentCount = studentCount;
    },
    setHasSeenOnboarding(state, newValue) {
      state.user.hasSeenOnboarding = newValue;
    },
  },
  getters: {
    environmentTerms: (state) => {
      return state.environmentTerms;
    },
    user: (state) => {
      return state.user;
    },
    currentCourses: (state) => {
      return state.user.courseMembership.filter((course) => {
        return course.active;
      });
    },
    unpublishedCourses(state, getters) {
      return getters.currentCourses.filter((course) => !course.published);
    },
    publishedCourses(state, getters) {
      return getters.currentCourses.filter((course) => course.published);
    },
    archivedCourses(state) {
      return state.user.courseMembership.filter((course) => !course.active);
    },
    // Sets value to true if there are students in the state, false if empty
    studentsLoaded: (state) => !!state.students.length,
    students: (state) => {
      return state.students;
    },
    studentCount: (state) => {
      return state.studentCount;
    },
    gradebookStudents: (state) => {
      return state.gradebookStudents;
    },
    gradebookAssignments: (state) => {
      return state.gradebookAssignments;
    },
    newActivity: (state) => {
      return state.newActivity;
    },
    hasSeenOnboarding: (state) => {
      return state.user.hasSeenOnboarding;
    },
    userGuideStatus: (state) => {
      return state.user.showGuide;
    },
    userCourseMemberships: (state) => {
      return state.user.courseMembership;
    },
    userActiveCourseMemberships: (state) => {
      return state.user.courseMembership.filter((course) => course.active);
    },
    isAdmin: (state) => {
      return state.user.admin;
    },
    isStaff: (state) => {
      return (
        state.user.role == "staff" ||
        state.user.role == "professor" ||
        state.user.role == "gsi"
      );
    },
    isStudent: (state) => {
      return state.user.role == "student";
    },
    isObserver: (state) => {
      return state.user.role == "observer";
    },
  },
});

export default store;
